export const consumptionTreatmentPtBr = {
  section: {
    title: 'Tratamento de CBS e de IBS',
    description:
      'Como o contribuinte é tratado em cada tributo de consumo, por vigência. A opção do Simples muda por semestre e por tributo.',
    loading: 'Carregando o tratamento de CBS e de IBS…',
    loadError: 'Não foi possível carregar o tratamento de CBS e de IBS.',
    refreshError:
      'Não foi possível atualizar o tratamento de CBS e de IBS. O que você digitou foi mantido.',
    retry: 'Tentar novamente',
  },
  tabPending: 'configuração pendente',
  notices: {
    noTaxpayer:
      'Defina o contribuinte em Informações básicas para registrar o tratamento de CBS e de IBS.',
    branch:
      'O tratamento de CBS e de IBS é do contribuinte e se edita na matriz. Aqui você só consulta.',
    branchLink: 'Abrir a matriz',
    noPermission: 'Você não tem permissão para ver o tratamento de CBS e de IBS.',
    readOnly: 'Você pode consultar o tratamento, mas não alterá-lo.',
  },
  tax: {
    CBS: 'CBS',
    IBS: 'IBS',
  },
  pending2027: 'Falta registrar o tratamento a partir de 01/01/2027.',
  empty: 'Nenhum tratamento registrado. Enquanto não houver, o tributo fica sem classificação.',
  treatment: {
    SIMPLES_INSIDE_DAS: 'Simples: dentro do DAS',
    SIMPLES_REGULAR_OPTION: 'Simples: opção pelo regime regular',
    REGULAR: 'Regime regular',
    SIMPLES_SUBLIMIT_EXCEEDED: 'Simples: sublimite excedido',
    OUT_OF_SCOPE: 'Fora do escopo',
  },
  eventType: {
    REQUESTED: 'Pedido',
    GRANTED: 'Deferimento',
    RENOUNCED: 'Renúncia',
    WITHDRAWN: 'Desistência',
  },
  segment: {
    periodClosed: '{{from}} a {{to}}',
    periodOpen: 'Desde {{from}}',
    reason: 'Motivo',
    evidence: 'Prova',
    codeAndDescription: '{{code}}: {{description}}',
    events: 'Eventos de opção',
    eventLine: '{{type}} em {{date}}',
  },
  actions: {
    add: 'Registrar tratamento',
    edit: 'Editar',
    addEvent: 'Registrar evento',
    save: 'Salvar tratamento',
    saveEvent: 'Salvar evento',
    cancel: 'Cancelar',
  },
  form: {
    addTitle: 'Registrar tratamento de {{tax}}',
    editTitle: 'Editar tratamento de {{tax}}',
    treatment: 'Tratamento',
    treatmentPlaceholder: 'Escolha o tratamento',
    validFrom: 'Início da vigência',
    validTo: 'Fim da vigência',
    optional: 'opcional',
    datePlaceholder: 'dd/mm/aaaa',
    dateInvalid: 'Informe uma data válida.',
    semesterHint: 'Em 2027, a opção do Simples começa em 1º de janeiro ou em 1º de julho.',
    reasonCode: 'Código do motivo jurídico',
    reasonDescription: 'Motivo jurídico',
    evidenceCode: 'Código da prova',
    evidenceDescription: 'Descrição da prova',
    required: 'Preencha este campo.',
    successorNote: 'O tratamento em vigor desde {{from}} termina em {{to}}.',
    requiredLegend: '* obrigatório',
  },
  eventForm: {
    title: 'Registrar evento de opção',
    eventType: 'Evento',
    eventTypePlaceholder: 'Escolha o evento',
    occurredOn: 'Data do evento',
    evidenceCode: 'Código da prova',
    evidenceDescription: 'Descrição da prova',
  },
  feedback: {
    treatmentSaved: 'Tratamento salvo',
    eventSaved: 'Evento registrado',
  },
  errors: {
    generic: 'Não foi possível salvar. Tente novamente.',
  },
} as const;

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };

export const consumptionTreatmentEn: Widen<typeof consumptionTreatmentPtBr> = {
  section: {
    title: 'CBS and IBS treatment',
    description:
      'How the taxpayer is treated for each consumption tax, by validity period. The Simples option changes by semester and by tax.',
    loading: 'Loading the CBS and IBS treatment…',
    loadError: 'The CBS and IBS treatment could not be loaded.',
    refreshError: 'The CBS and IBS treatment could not be refreshed. What you typed is kept.',
    retry: 'Try again',
  },
  tabPending: 'setup pending',
  notices: {
    noTaxpayer: 'Set the taxpayer in Basic information to register the CBS and IBS treatment.',
    branch:
      'The CBS and IBS treatment belongs to the taxpayer and is edited on the head office. Here you can only read it.',
    branchLink: 'Open the head office',
    noPermission: 'You do not have permission to see the CBS and IBS treatment.',
    readOnly: 'You can read the treatment but not change it.',
  },
  tax: {
    CBS: 'CBS',
    IBS: 'IBS',
  },
  pending2027: 'The treatment from 01/01/2027 is not registered yet.',
  empty: 'No treatment registered. Until there is one, the tax stays unclassified.',
  treatment: {
    SIMPLES_INSIDE_DAS: 'Simples: inside the DAS',
    SIMPLES_REGULAR_OPTION: 'Simples: option for the regular regime',
    REGULAR: 'Regular regime',
    SIMPLES_SUBLIMIT_EXCEEDED: 'Simples: sublimit exceeded',
    OUT_OF_SCOPE: 'Out of scope',
  },
  eventType: {
    REQUESTED: 'Request',
    GRANTED: 'Grant',
    RENOUNCED: 'Renunciation',
    WITHDRAWN: 'Withdrawal',
  },
  segment: {
    periodClosed: '{{from}} to {{to}}',
    periodOpen: 'Since {{from}}',
    reason: 'Reason',
    evidence: 'Evidence',
    codeAndDescription: '{{code}}: {{description}}',
    events: 'Option events',
    eventLine: '{{type}} on {{date}}',
  },
  actions: {
    add: 'Register treatment',
    edit: 'Edit',
    addEvent: 'Record event',
    save: 'Save treatment',
    saveEvent: 'Save event',
    cancel: 'Cancel',
  },
  form: {
    addTitle: 'Register {{tax}} treatment',
    editTitle: 'Edit {{tax}} treatment',
    treatment: 'Treatment',
    treatmentPlaceholder: 'Choose the treatment',
    validFrom: 'Validity start',
    validTo: 'Validity end',
    optional: 'optional',
    datePlaceholder: 'dd/mm/yyyy',
    dateInvalid: 'Enter a valid date.',
    semesterHint: 'In 2027, the Simples option starts on January 1 or July 1.',
    reasonCode: 'Legal reason code',
    reasonDescription: 'Legal reason',
    evidenceCode: 'Evidence code',
    evidenceDescription: 'Evidence description',
    required: 'Fill in this field.',
    successorNote: 'The treatment in force since {{from}} ends on {{to}}.',
    requiredLegend: '* required',
  },
  eventForm: {
    title: 'Record option event',
    eventType: 'Event',
    eventTypePlaceholder: 'Choose the event',
    occurredOn: 'Event date',
    evidenceCode: 'Evidence code',
    evidenceDescription: 'Evidence description',
  },
  feedback: {
    treatmentSaved: 'Treatment saved',
    eventSaved: 'Event recorded',
  },
  errors: {
    generic: 'It could not be saved. Try again.',
  },
};
